/**
 * Nén lại ảnh bằng MozJPEG (WASM) ngay trên máy khách, trước khi gửi lên đơn.
 *
 * Vì sao KHÔNG dùng chung `image-downscale.ts`: hai chỗ dùng hai bản ảnh khác
 * nhau. Ảnh bảng kiểm ca là chứng cứ đọc-một-lần, thu về 1600px là xong. Còn
 * ảnh buổi chụp thì bản `orig` trong kho CHÍNH LÀ thứ hậu kỳ mở ra sửa và
 * khách bấm "tải bản gốc" — thu nhỏ nó là mất, không có đường lùi. Nên ở đây
 * mặc định GIỮ NGUYÊN độ phân giải và chỉ mã hoá lại ở chất lượng cao.
 *
 * Vì sao MozJPEG chứ không phải `canvas.toBlob`: toBlob giao chất lượng cho
 * trình duyệt, mỗi máy một kiểu, và không có trellis. MozJPEG là nhánh của
 * libjpeg-turbo mà `cjpeg` dùng, nên bộ tham số dưới đây đọc thẳng ra cờ dòng
 * lệnh: quality → -quality, progressive → -progressive, trellis_* → -tune.
 *
 * Nguyên tắc AN TOÀN (giống image-downscale.ts): mọi nhánh hỏng đều TRẢ LẠI
 * FILE GỐC, không ném lỗi. Nén chỉ là tối ưu băng thông; làm hỏng ảnh buổi
 * chụp vì tối ưu thất bại thì mất ảnh của khách.
 */

import { reencode, SKIP_BELOW_BYTES } from "./jpeg-recompress-core";

export { QUALITY, SKIP_BELOW_BYTES, MAX_DECODED_PIXELS, computeTargetSize } from "./jpeg-recompress-core";

/** Trình duyệt này có đủ đồ để giải mã và vẽ lại ảnh không. */
function canRedraw(): boolean {
  if (typeof document === "undefined" || typeof createImageBitmap !== "function") return false;
  if (typeof WebAssembly === "undefined") return false;
  const c = document.createElement("canvas");
  return typeof c.getContext === "function";
}

/**
 * Loại tệp đưa vào MozJPEG được. HEIC vắng mặt có chủ ý: Safari giải mã được
 * nên `createImageBitmap` nuốt trôi trên iOS, nhưng Chrome/Android thì không —
 * ở đó nhánh catch trả lại file gốc và máy chủ đáp đúng câu "HEIC phải đổi
 * sang JPEG trước" (albumupload.go). Ta không giả vờ hỗ trợ nửa vời.
 */
const RECOMPRESSIBLE = new Set(["image/jpeg", "image/png", "image/webp"]);

/**
 * Nạp encoder MozJPEG theo nhu cầu.
 *
 * `import()` động chứ không phải import tĩnh: gói mang theo ~59 KB gzip WASM,
 * mà ngân sách gói vào (300 KB gzip, scripts/check-bundle-budget.cjs) tính
 * trên chunk khởi động. Người xem báo cáo không phải tải bộ mã hoá ảnh.
 */
async function loadEncoder() {
  const mod = await import("@jsquash/jpeg/encode");
  return mod.default;
}

/**
 * Luồng ngầm có đủ đồ không: Worker + OffscreenCanvas (Chrome 69+, Safari 16.4+,
 * Firefox 105+). Thiếu thì nén trên trang như trước 24/10/2026 — chậm và đơ,
 * nhưng vẫn đỡ băng thông.
 */
function canUseWorker(): boolean {
  return (
    typeof Worker !== "undefined" &&
    typeof OffscreenCanvas !== "undefined" &&
    typeof createImageBitmap === "function"
  );
}

/**
 * Trần thời gian một tấm trong luồng ngầm. Đo 24/10/2026: tấm 64 MP mất 22 giây
 * trên máy chậm gấp 4 máy văn phòng; 5 phút là quá xa mọi tấm thật, nhưng vẫn
 * hữu hạn — luồng treo thì giữ ảnh gốc và lượt tải đi tiếp.
 */
const WORKER_TIMEOUT_MS = 5 * 60_000;

function abortError(): DOMException {
  return new DOMException("Đã tạm dừng tải ảnh", "AbortError");
}

/**
 * Nén một tấm trong luồng ngầm RIÊNG rồi đóng luồng: bộ nhớ WASM phình theo tấm
 * to nhất và không bao giờ co lại, giữ một luồng cho cả lô là giữ chỗ đó mãi.
 * `null` = không nén được (giữ gốc). Ném AbortError khi `signal` bị huỷ.
 */
function reencodeInWorker(file: File, maxEdge: number, signal?: AbortSignal): Promise<ArrayBuffer | null> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    let worker: Worker;
    try {
      worker = new Worker(new URL("./jpeg-recompress.worker.ts", import.meta.url), { type: "module" });
    } catch {
      resolve(null);
      return;
    }
    const finish = (buffer: ArrayBuffer | null, error?: DOMException) => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      worker.onmessage = null;
      worker.onerror = null;
      worker.onmessageerror = null;
      worker.terminate();
      if (error) reject(error);
      else resolve(buffer);
    };
    const onAbort = () => finish(null, abortError());
    const timer = setTimeout(() => finish(null), WORKER_TIMEOUT_MS);
    signal?.addEventListener("abort", onAbort, { once: true });
    worker.onmessage = (event: MessageEvent<{ buffer?: unknown }>) =>
      finish(event.data?.buffer instanceof ArrayBuffer ? event.data.buffer : null);
    worker.onerror = () => finish(null);
    worker.onmessageerror = () => finish(null);
    worker.postMessage({ file, maxEdge });
  });
}

/** Đường cũ trên trang — chỉ cho trình duyệt thiếu luồng ngầm vẽ được. */
async function reencodeOnPage(file: File, maxEdge: number): Promise<ArrayBuffer | null> {
  let encode: Awaited<ReturnType<typeof loadEncoder>>;
  try {
    encode = await loadEncoder();
  } catch {
    return null;
  }
  return reencode(file, maxEdge, {
    getContext: (width, height) => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      // `willReadFrequently` vì bước kế tiếp là getImageData — không có cờ này
      // Chrome giữ canvas trên GPU rồi phải kéo ngược về, chậm hơn hẳn.
      return canvas.getContext("2d", { willReadFrequently: true });
    },
    encode,
  });
}

/**
 * Mã hoá lại `file` thành JPEG chất lượng {@link QUALITY}, GIỮ thông số máy ảnh
 * (EXIF) của ảnh gốc (CEO chốt 2a, 24/10/2026). Nén trong luồng ngầm khi trình
 * duyệt có — trang không đứng hình (CEO chốt 1a cùng ngày).
 *
 * @param maxEdge Cạnh dài tối đa. Bỏ trống hoặc 0 → GIỮ NGUYÊN độ phân giải,
 *   đây là mặc định cho ảnh buổi chụp vì bản này là bản hậu kỳ dùng.
 * @param signal Huỷ giữa chừng (nút Tạm dừng): đóng luồng ngầm ngay và ném
 *   AbortError. Đây là lỗi DUY NHẤT hàm này ném.
 *
 * Trả lại chính `file` khi: không phải loại nén được, ảnh đã nhỏ, ảnh vượt
 * trần điểm ảnh của máy chủ, trình duyệt thiếu đồ, kết quả không nhỏ hơn gốc,
 * hoặc bất kỳ lỗi nào giữa chừng.
 */
export async function recompressJpeg(file: File, maxEdge = 0, signal?: AbortSignal): Promise<File> {
  if (!RECOMPRESSIBLE.has(file.type)) return file;
  if (file.size <= SKIP_BELOW_BYTES) return file;

  let buf: ArrayBuffer | null;
  if (canUseWorker()) buf = await reencodeInWorker(file, maxEdge, signal);
  else if (canRedraw()) buf = await reencodeOnPage(file, maxEdge);
  else return file;

  // Mã hoá lại ra tệp KHÔNG nhỏ hơn thì giữ bản gốc. Đây là nhánh THƯỜNG
  // GẶP khi giữ nguyên độ phân giải, không phải nhánh hiếm: ảnh máy ảnh xuất
  // ra vốn đã quanh mức này rồi, mã hoá lại chẳng còn gì để cắt. Giữ gốc vừa
  // nhẹ hơn vừa không mất một đời mã hoá.
  if (!buf || buf.byteLength >= file.size) return file;

  return new File([buf], renameToJpg(file.name), {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
}

/** "IMG_0421.PNG" → "IMG_0421.jpg". Tên rỗng thì đặt tên mặc định. */
export function renameToJpg(name: string): string {
  const base = name.replace(/\.[^.]+$/, "").trim();
  return (base || "anh-buoi-chup") + ".jpg";
}
